import { IApi } from '@/setup/api-client'
import { useApiQuery } from '@/utils/api/api-query'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import type { ListAdBoardsData } from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/boards'

export type IAdBoard = ListAdBoardsData[number]

export type IGetAdBoardsQueryParams = {
    organizationId: string
    brandSlug: string
    archived?: boolean
    /** When set, each board reports whether it already holds this ad. */
    forAdUuid?: string
}

export class GetAdBoardsQuery {
    static getKey(params: IGetAdBoardsQueryParams) {
        return ['ad-boards', params.brandSlug, params.archived ? 'archived' : 'active', params.forAdUuid ?? null]
    }

    static async call(api: IApi, params: IGetAdBoardsQueryParams) {
        const search = new URLSearchParams()
        if (params.archived) search.set('archived', 'true')
        if (params.forAdUuid) search.set('adUuid', params.forAdUuid)
        const query = search.toString() ? `?${search.toString()}` : ''
        const res = await api.get<{ data: ListAdBoardsData }>(
            `/organization/${params.organizationId}/brands/${params.brandSlug}/boards${query}`
        )
        return (res as unknown as { data: ListAdBoardsData }).data
    }
}

export function useGetAdBoardsQuery(options: { archived?: boolean; forAdUuid?: string } = {}) {
    const { api, apiParams } = useApiToolbox()
    const params = { ...apiParams, archived: options.archived, forAdUuid: options.forAdUuid }

    return useApiQuery({
        queryKey: GetAdBoardsQuery.getKey(params),
        queryFn: () => GetAdBoardsQuery.call(api, params),
        enabled: !!apiParams.organizationId && !!apiParams.brandSlug,
        // Boards change through in-app mutations that invalidate explicitly, so a revisit
        // should render from cache instantly. The default gcTime (5m) dropped the unmounted
        // cache entry between visits, turning every return into a cold spinner load; keeping
        // it longer means the app-default staleTime (10m) governs background refresh instead.
        gcTime: 30 * 60 * 1000,
    })
}
